
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, Plus, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { SubscriptionPaymentDialog } from "../athlete/subscription/SubscriptionPaymentDialog";

interface ProfilePaymentMethodsProps {
  userId?: string;
}

interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

export const ProfilePaymentMethods: React.FC<ProfilePaymentMethodsProps> = ({ userId }) => {
  const { t } = useTranslation(["profile", "common"]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);


  // Mock function to simulate getting payment methods from Stripe
  const fetchPaymentMethods = async () => {
    // This would be replaced with actual Stripe integration
    setIsLoading(true);
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock data for demonstration
      // setPaymentMethods([
      //   {
      //     id: "pm_123456789",
      //     brand: "visa",
      //     last4: "4242",
      //     expMonth: 12,
      //     expYear: 2025
      //   },
      //   {
      //     id: "pm_987654321",
      //     brand: "mastercard",
      //     last4: "5555",
      //     expMonth: 9,
      //     expYear: 2024
      //   }
      // ]);
      
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      toast.error(t("errors.loadingPaymentMethods", { ns: "profile" }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    try {
      // This would be replaced with actual Stripe Customer Portal session creation
      toast.info(t("info.redirectToStripe", { ns: "profile" }));
      
      // Mock customer portal URL - would come from a Supabase Edge Function
      // const portalUrl = "https://billing.stripe.com/p/customer/portal";
      // window.open(portalUrl, "_blank");

      
    } catch (error) {
      console.error("Error creating customer portal session:", error);
      toast.error(t("errors.stripeRedirect", { ns: "profile" }));
    }

    setIsPaymentDialogOpen(false);

  };

  const handleDeletePaymentMethod = (paymentMethodId: string) => {
    // This would be replaced with actual Stripe deletion
    toast.info(t("info.paymentMethodDeleted", { ns: "profile" }));
    setPaymentMethods(methods => methods.filter(m => m.id !== paymentMethodId));
  };

  React.useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const getCardIcon = (brand: string) => {
    switch(brand.toLowerCase()) {
      case "visa":
        return "💳 Visa";
      case "mastercard":
        return "💳 MasterCard";
      case "amex":
        return "💳 Amex";
      default:
        return "💳 Card";
    }
  };

  return (
    <Card className="border shadow-sm">
      <CardHeader>
        <CardTitle>{t("payment.title", { ns: "profile" })}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {isLoading ? (
            <p>{t("loading", { ns: "common" })}</p>
          ) : paymentMethods.length === 0 ? (
            <div className="text-center p-6">
              <CreditCard className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="font-semibold text-lg mb-1">{t("payment.noMethods", { ns: "profile" })}</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t("payment.addMethodDescription", { ns: "profile" })}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {paymentMethods.map(method => (
                <div 
                  key={method.id}
                  className="flex items-center justify-between p-4 bg-card border rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-xl">{getCardIcon(method.brand)}</div>
                    <div>
                      <p className="font-medium">
                        {method.brand.charAt(0).toUpperCase() + method.brand.slice(1)} •••• {method.last4}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t("payment.expires", { ns: "profile" })}: {method.expMonth}/{method.expYear}
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleDeletePaymentMethod(method.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <Button 
            variant="outline" 
            className="w-full"
            onClick={()=>setIsPaymentDialogOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            {t("payment.addMethod", { ns: "profile" })}
          </Button>
           
        </div>
      </CardContent>
    </Card>
  );
};
